// @ts-nocheck
import * as THREE from "three";
import {
  extractSkinTextureUrl
} from "../core/blockEntityData.ts";

const DYE_COLORS = {
  white: "#f9fffe",
  orange: "#f9801d",
  magenta: "#c74ebd",
  light_blue: "#3ab3da",
  yellow: "#fed83d",
  lime: "#80c71f",
  pink: "#f38baa",
  gray: "#474f52",
  light_gray: "#9d9d97",
  cyan: "#169c9c",
  purple: "#8932b8",
  blue: "#3c44aa",
  brown: "#835432",
  green: "#5e7c16",
  red: "#b02e26",
  black: "#1d1d21"
};

function rotateGeometryForState(geometry, blockId, state) {
  if (blockId.includes("wall_") || state.facing) {
    const yaw = {
      north: 0,
      east: -Math.PI / 2,
      south: Math.PI,
      west: Math.PI / 2
    }[state.facing || "north"] || 0;
    geometry.rotateY(yaw);
    return geometry;
  }

  if (state.rotation !== undefined) {
    const steps = Number(state.rotation) || 0;
    geometry.rotateY(-steps * (Math.PI * 2 / 16));
  }

  return geometry;
}

function createTextCanvas(sign) {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 128;
  const context = canvas.getContext("2d", { alpha: true });
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.font = "bold 24px monospace";

  const side = sign.front;
  const color = DYE_COLORS[side.color] || side.color || "#1d1d21";

  if (side.glowing) {
    context.shadowColor = color;
    context.shadowBlur = 10;
  }

  context.fillStyle = color;
  side.lines.forEach((line, index) => {
    context.fillText(String(line || ""), 128, 20 + index * 29);
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function createSignTextBundle(blockEntry) {
  const sign = blockEntry.blockEntity?.sign;
  if (!sign) return null;

  const geometry = new THREE.PlaneGeometry(
    blockEntry.blockId.includes("hanging_sign") ? 0.88 : 1.18,
    blockEntry.blockId.includes("hanging_sign") ? 0.52 : 0.58
  );
  geometry.translate(
    0,
    blockEntry.blockId.includes("hanging_sign") ? 0.08 : 0.18,
    0.505
  );
  rotateGeometryForState(
    geometry,
    blockEntry.blockId,
    blockEntry.state || {}
  );

  const texture = createTextCanvas(sign);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    alphaTest: 0.02,
    side: THREE.DoubleSide,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -2
  });

  return {
    signature:
      "block-entity-sign|" +
      blockEntry.blockId +
      "|" +
      JSON.stringify(blockEntry.state || {}) +
      "|" +
      JSON.stringify(sign),
    geometry,
    materials: material,
    model: "block-entity:sign-text",
    blockEntityOverlay: true,
    ownsGeometry: true,
    ownsMaterial: true
  };
}

function tintCanvasLayer(source, color) {
  const canvas = document.createElement("canvas");
  canvas.width = source.width;
  canvas.height = source.height;
  const context = canvas.getContext("2d", { alpha: true });
  context.clearRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, 0);
  context.globalCompositeOperation = "source-in";
  context.fillStyle = DYE_COLORS[color] || color || "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.globalCompositeOperation = "source-over";
  return canvas;
}

async function createBannerPatternTexture(assetLoader, banner) {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext("2d", { alpha: true });
  context.clearRect(0, 0, 64, 64);

  for (const layer of banner.patterns || []) {
    const name = String(layer.pattern || "")
      .replace(/^minecraft:/, "");
    const path = assetLoader.findTexturePath([
      "entity/banner/" + name,
      "entity/banner_patterns/" + name
    ]);

    if (!path) continue;

    try {
      const texture = await assetLoader.loadTexture(path);
      const image = texture.image;
      if (!image) continue;
      const tinted = tintCanvasLayer(image, layer.color);
      context.drawImage(tinted, 0, 0, 64, 64);
    } catch (error) {
      console.warn(
        "Unable to render banner pattern layer " + name + ".",
        error
      );
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function createBannerOverlayBundle(assetLoader, blockEntry) {
  const banner = blockEntry.blockEntity?.banner;
  if (!banner || !banner.patterns?.length) return null;

  const wall = blockEntry.blockId.endsWith("_wall_banner");
  const geometry = new THREE.PlaneGeometry(1.24, 2.45);
  geometry.translate(0, 0.86, wall ? 0.505 : 0.065);
  rotateGeometryForState(
    geometry,
    blockEntry.blockId,
    blockEntry.state || {}
  );

  const material = new THREE.MeshBasicMaterial({
    transparent: true,
    alphaTest: 0.02,
    side: THREE.DoubleSide,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -3
  });

  createBannerPatternTexture(assetLoader, banner)
    .then((texture) => {
      material.map = texture;
      material.needsUpdate = true;
    });

  return {
    signature:
      "block-entity-banner|" +
      blockEntry.blockId +
      "|" +
      JSON.stringify(blockEntry.state || {}) +
      "|" +
      JSON.stringify(banner),
    geometry,
    materials: material,
    model: "block-entity:banner-patterns",
    blockEntityOverlay: true,
    ownsGeometry: true,
    ownsMaterial: true
  };
}

function addFace(
  positions,
  uvs,
  corners,
  uvRect,
  textureWidth = 64,
  textureHeight = 64
) {
  const [u0, v0, u1, v1] = uvRect;
  const faceUvs = [
    [u0 / textureWidth, 1 - v1 / textureHeight],
    [u1 / textureWidth, 1 - v1 / textureHeight],
    [u1 / textureWidth, 1 - v0 / textureHeight],
    [u0 / textureWidth, 1 - v0 / textureHeight]
  ];
  const order = [0, 1, 2, 0, 2, 3];

  order.forEach((index) => {
    positions.push(...corners[index]);
    uvs.push(...faceUvs[index]);
  });
}

function createPlayerHeadCube(scale, uvBaseX, uvBaseY) {
  const half = scale / 2;
  const y1 = -0.5;
  const y2 = -0.5 + scale;
  const x1 = -half;
  const x2 = half;
  const z1 = -half;
  const z2 = half;
  const positions = [];
  const uvs = [];

  addFace(
    positions,
    uvs,
    [[x2,y1,z1],[x1,y1,z1],[x1,y2,z1],[x2,y2,z1]],
    [uvBaseX + 8, uvBaseY + 8, uvBaseX + 16, uvBaseY + 16]
  );
  addFace(
    positions,
    uvs,
    [[x1,y1,z2],[x2,y1,z2],[x2,y2,z2],[x1,y2,z2]],
    [uvBaseX + 24, uvBaseY + 8, uvBaseX + 32, uvBaseY + 16]
  );
  addFace(
    positions,
    uvs,
    [[x1,y1,z1],[x1,y1,z2],[x1,y2,z2],[x1,y2,z1]],
    [uvBaseX + 16, uvBaseY + 8, uvBaseX + 24, uvBaseY + 16]
  );
  addFace(
    positions,
    uvs,
    [[x2,y1,z2],[x2,y1,z1],[x2,y2,z1],[x2,y2,z2]],
    [uvBaseX, uvBaseY + 8, uvBaseX + 8, uvBaseY + 16]
  );
  addFace(
    positions,
    uvs,
    [[x1,y2,z2],[x2,y2,z2],[x2,y2,z1],[x1,y2,z1]],
    [uvBaseX + 8, uvBaseY, uvBaseX + 16, uvBaseY + 8]
  );
  addFace(
    positions,
    uvs,
    [[x1,y1,z1],[x2,y1,z1],[x2,y1,z2],[x1,y1,z2]],
    [uvBaseX + 16, uvBaseY, uvBaseX + 24, uvBaseY + 8]
  );

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(positions, 3)
  );
  geometry.setAttribute(
    "uv",
    new THREE.Float32BufferAttribute(uvs, 2)
  );
  geometry.computeVertexNormals();
  return geometry;
}

function createPlayerHeadGeometry(blockEntry) {
  const base = createPlayerHeadCube(0.5, 0, 0);
  const outer = createPlayerHeadCube(0.53, 32, 0);

  const basePosition = base.getAttribute("position");
  const outerPosition = outer.getAttribute("position");
  const baseUv = base.getAttribute("uv");
  const outerUv = outer.getAttribute("uv");

  const positions = new Float32Array(
    basePosition.array.length + outerPosition.array.length
  );
  positions.set(basePosition.array, 0);
  positions.set(outerPosition.array, basePosition.array.length);

  const uvs = new Float32Array(baseUv.array.length + outerUv.array.length);
  uvs.set(baseUv.array, 0);
  uvs.set(outerUv.array, baseUv.array.length);

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.BufferAttribute(positions, 3)
  );
  geometry.setAttribute(
    "uv",
    new THREE.BufferAttribute(uvs, 2)
  );
  geometry.computeVertexNormals();

  if (blockEntry.blockId === "player_wall_head") {
    geometry.translate(0, 0.18, -0.24);
  } else {
    geometry.translate(0, 0.04, 0);
  }

  rotateGeometryForState(
    geometry,
    blockEntry.blockId,
    blockEntry.state || {}
  );

  base.dispose();
  outer.dispose();
  return geometry;
}

function resolvePlayerHeadUrl(blockEntry) {
  const head = blockEntry.blockEntity?.playerHead;
  if (!head) return null;
  return (
    head.textureUrl?.trim() ||
    extractSkinTextureUrl(head.textureValue) ||
    null
  );
}

function createPlayerHeadBundle(assetLoader, blockEntry) {
  const textureUrl = resolvePlayerHeadUrl(blockEntry);
  if (!textureUrl) return null;

  const geometry = createPlayerHeadGeometry(blockEntry);
  const material = new THREE.MeshStandardMaterial({
    color: "#ffffff",
    roughness: 0.88,
    transparent: true,
    alphaTest: 0.02
  });

  assetLoader
    .loadExternalTexture(textureUrl)
    .then((texture) => {
      material.map = texture;
      material.needsUpdate = true;
    })
    .catch((error) => {
      console.warn(
        "Unable to load player head skin texture.",
        error
      );
    });

  return {
    signature:
      "block-entity-player-head|" +
      blockEntry.blockId +
      "|" +
      JSON.stringify(blockEntry.state || {}) +
      "|" +
      textureUrl,
    geometry,
    materials: material,
    model: "block-entity:player-head",
    blockEntityOverlay: false,
    ownsGeometry: true,
    ownsMaterial: true
  };
}

export class MinecraftBlockEntityPreviewRenderer {
  constructor(assetLoader) {
    this.assetLoader = assetLoader;
    this.ownedBundles = new Set();
  }

  shouldReplaceBase(blockEntry) {
    return Boolean(
      blockEntry.blockEntity?.playerHead &&
      (
        blockEntry.blockId === "player_head" ||
        blockEntry.blockId === "player_wall_head"
      ) &&
      resolvePlayerHeadUrl(blockEntry)
    );
  }

  createReplacementParts(blockEntry) {
    const headBundle = createPlayerHeadBundle(
      this.assetLoader,
      blockEntry
    );
    if (!headBundle) return [];
    this.ownedBundles.add(headBundle);
    return [headBundle];
  }

  createOverlayParts(blockEntry) {
    const parts = [];

    const signBundle = createSignTextBundle(blockEntry);
    if (signBundle) {
      this.ownedBundles.add(signBundle);
      parts.push(signBundle);
    }

    const bannerBundle = createBannerOverlayBundle(
      this.assetLoader,
      blockEntry
    );
    if (bannerBundle) {
      this.ownedBundles.add(bannerBundle);
      parts.push(bannerBundle);
    }

    return parts;
  }

  dispose() {
    this.ownedBundles.forEach((bundle) => {
      if (bundle.ownsGeometry) {
        bundle.geometry.dispose();
      }
      if (bundle.ownsMaterial) {
        const materials = Array.isArray(bundle.materials)
          ? bundle.materials
          : [bundle.materials];
        materials.forEach((material) => {
          if (material.map) material.map.dispose();
          material.dispose();
        });
      }
    });
    this.ownedBundles.clear();
  }
}
